import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Portfolio.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

const projects = [
  {
    title: "Site internet pour une Coach Sportive",
    category: "web",
    tags: ["Vercel", "React", "SEO"],
    image: "portfolio-nono.png",
    link: "https://www.noemie-coach.fr/"
  },
    {
    title: "QIZUNA",
    category: "web",
    tags: ["SEO", "Framer Motion", "React"],
    image: "portfolio-qizuna.webp",
    link: "https://qizuna.fr/"
  },
  {
    title: "Site pour une agence d'évènementiel - 724 Events",
    category: "web",
    tags: ["Performances", "Accessibilité", "SEO"],
    image: "portfolio-724events.png",
    link: "https://github.com/Tatitatouu/Neuvi-meProjet"
  },
  {
    title: "Portfolio d'une Architecte - Sophie Bluel",
    category: "design",
    tags: ["Front-end", "Back-end", "API"],
    image: "portfolio-sophiebluel.jpg",
    link: "https://github.com/Tatitatouu/Sixi-meProjet"
  },
  {
    title: "Oh My Food",
    category: "dev",
    tags: ["Responsive", "Animation", "SASS"],
    image: "portfolio-ohmyfood.png",
    link: "https://tatitatouu.github.io/Quatri-meProjet/"
  }
];

const filters = [
  { label: 'Tous', value: 'all' },
  { label: 'Sites Web', value: 'web' },
  { label: 'Design', value: 'design' },
  { label: 'Développement', value: 'dev' },
];

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const filteredProjects = useMemo(() => {
    if (activeFilter === 'all') return projects;
    return projects.filter(p => p.category === activeFilter);
  }, [activeFilter]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.9
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    hover: {
      y: -8,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  const filterVariants = {
    inactive: { 
      scale: 1,
      backgroundColor: "rgba(255, 255, 255, 0.8)"
    },
    active: { 
      scale: 1.05,
      backgroundColor: "#d1a6c6",
      transition: { duration: 0.3 }
    },
    hover: {
      scale: 1.02,
      backgroundColor: "rgba(209, 166, 198, 0.1)",
      transition: { duration: 0.2 }
    }
  };

  return (
    <section id="portfolio" className="portfolio">
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="portfolio-header"
      >
        <h2 className="portfolio-title">Portfolio</h2>
        <p className="portfolio-subtitle">Découvrez mes projets récents</p>
      </motion.div>

      <motion.div 
        className="portfolio-filter"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {filters.map(filter => (
          <motion.button
            key={filter.value}
            className={`filter-btn${activeFilter === filter.value ? ' active' : ''}`}
            onClick={() => setActiveFilter(filter.value)}
            type="button"
            variants={filterVariants}
            initial="inactive"
            animate={activeFilter === filter.value ? "active" : "inactive"}
            whileHover="hover"
            whileTap={{ scale: 0.95 }}
          >
            {filter.label}
          </motion.button>
        ))}
      </motion.div>

      <motion.div 
        className="portfolio-grid"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <AnimatePresence mode="wait">
          {filteredProjects.map((project, idx) => (
            <motion.div
              className="portfolio-card"
              data-category={project.category}
              key={`${project.title}-${activeFilter}`}
              variants={cardVariants}
              whileHover="hover"
              layout
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <div className="card-inner">
                <div className="card-front">
                  <div className="image-container">
                    <img
                      src={process.env.PUBLIC_URL + '/Portfolio/' + project.image}
                      alt={project.title}
                      className="portfolio-image"
                    />
                    <div className="image-overlay"></div>
                  </div>
                  <div className="card-content">
                    <h3 className="portfolio-name">{project.title}</h3>
                    <div className="tags-preview">
                      {project.tags.slice(0, 2).map(tag => (
                        <span className="tag-small" key={tag}>{tag}</span>
                      ))}
                      {project.tags.length > 2 && (
                        <span className="tag-more">+{project.tags.length - 2}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="card-back">
                  <div className="card-back-content">
                    <h3 className="project-title">{project.title}</h3>
                    <div className="tags-full">
                      {project.tags.map(tag => (
                        <span className="tag" key={tag}>{tag}</span>
                      ))}
                    </div>
                    <motion.a
                      href={project.link}
                      className="view-project"
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span>Voir le projet</span>
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </motion.a>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};

export default Portfolio;
